/**
 * Modal de Backups do Workspace — lista, restaura, deleta backups
 */
import { h } from 'preact';
import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

// ── Signals internos ──
const backups = signal<any[]>([]);
const loadingBackups = signal(false);
const actionLoading = signal('');
const confirmAction = signal<{ type: string; id: string; name: string } | null>(null);
const toast = signal<{ msg: string; type: 'success' | 'error' } | null>(null);

// ── Estilos ──
const colors = {
  bg: T.bg, card: T.bg, border: T.bgSecondary,
  green: T.success, red: T.danger, blue: T.primary, purple: '#a371f7',
  text: T.text, muted: T.textSecondary,
};

interface BackupModalProps {
  workspaceId: string;
  workspaceName: string;
  onClose: () => void;
}

function showToast(msg: string, type: 'success' | 'error' = 'success') {
  toast.value = { msg, type };
  setTimeout(() => { toast.value = null; }, 3000);
}

async function loadBackups(wsId: string) {
  loadingBackups.value = true;
  try {
    const res: any = await api.get(`/cloud/workspaces/${wsId}/backups`);
    backups.value = res.backups || [];
  } catch { backups.value = []; }
  loadingBackups.value = false;
}

async function handleRestore(wsId: string, backupId: string) {
  actionLoading.value = backupId;
  try {
    await api.post(`/cloud/workspaces/${wsId}/backups/${backupId}/restore`, {}, { timeout: 60000 });
    showToast('Backup restaurado com sucesso!');
  } catch (e: any) {
    showToast(e.message || 'Erro ao restaurar', 'error');
  }
  actionLoading.value = '';
  confirmAction.value = null;
}

async function handleDelete(wsId: string, backupId: string) {
  actionLoading.value = backupId;
  try {
    await api.delete(`/cloud/workspaces/${wsId}/backups/${backupId}`);
    backups.value = backups.value.filter((b: any) => b.id !== backupId);
    showToast('Backup deletado');
  } catch (e: any) {
    showToast(e.message || 'Erro ao deletar', 'error');
  }
  actionLoading.value = '';
  confirmAction.value = null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso + 'Z').toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export function BackupModal({ workspaceId, workspaceName, onClose }: BackupModalProps) {
  useEffect(() => { loadBackups(workspaceId); }, [workspaceId]);

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 16, padding: 24, width: '90%', maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: colors.text }}>📸 Backups — {workspaceName}</h2>
          <Button variant="ghost" onClick={onClose} style={{ background: 'none', border: 'none', color: colors.muted, fontSize: 20, cursor: 'pointer' }}>✕</Button>
        </div>

        {/* Toast */}
        {toast.value && (
          <div style={{
            padding: '8px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14,
            background: toast.value.type === 'success' ? T.successBg : T.dangerBg,
            color: toast.value.type === 'success' ? colors.green : colors.red,
            border: `1px solid ${toast.value.type === 'success' ? colors.green : colors.red}33`,
          }}>
            {toast.value.msg}
          </div>
        )}

        {/* Loading */}
        {loadingBackups.value && (
          <div style={{ padding: 16 }}>{[1,2,3].map(i => (<div key={i} style={{ background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, marginBottom: 8 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={12} /></div>))}</div>
        )}

        {/* Lista vazia */}
        {!loadingBackups.value && backups.value.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: colors.muted }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
            Nenhum backup encontrado para este workspace.
          </div>
        )}

        {/* Lista de backups */}
        {!loadingBackups.value && backups.value.map((b: any) => (
          <div key={b.id} style={{
            background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10,
            padding: 14, marginBottom: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15, color: colors.text }}>{b.name}</div>
                {b.description && <div style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>{b.description}</div>}
                <div style={{ fontSize: 12, color: colors.muted, marginTop: 6, display: 'flex', gap: 12 }}>
                  <span>📅 {formatDate(b.created_at)}</span>
                  <span>{formatSize(b.size_bytes)}</span>
                  <span>📄 {Object.keys(b.brain_files || {}).length} arquivos</span>
                  <span>{(b.skills || []).length} skills</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Button variant="ghost"
                  style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: `1px solid ${colors.green}44`, background: `${colors.green}15`, color: colors.green, cursor: 'pointer' }}
                  disabled={!!actionLoading.value}
                  onClick={() => { confirmAction.value = { type: 'restore', id: b.id, name: b.name }; }}
                >
                   Restaurar
                </Button>
                <Button variant="ghost"
                  style={{ padding: '4px 10px', fontSize: 12, borderRadius: 6, border: `1px solid ${colors.red}44`, background: `${colors.red}15`, color: colors.red, cursor: 'pointer' }}
                  disabled={!!actionLoading.value}
                  onClick={() => { confirmAction.value = { type: 'delete', id: b.id, name: b.name }; }}
                >
                  
                </Button>
              </div>
            </div>
          </div>
        ))}

        {/* Modal de confirmação */}
        {confirmAction.value && (
          <div style={{
            position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
          }} onClick={(e) => { if (e.target === e.currentTarget) confirmAction.value = null; }}>
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 24, maxWidth: 400, width: '90%' }}>
              <h3 style={{ margin: '0 0 12px', color: colors.text }}>
                {confirmAction.value.type === 'restore' ? ' Restaurar Backup' : 'Deletar Backup'}
              </h3>
              <p style={{ color: colors.muted, fontSize: 14, margin: '0 0 20px' }}>
                {confirmAction.value.type === 'restore'
                  ? `Tem certeza? A configuração atual do workspace será substituída pelo backup "${confirmAction.value.name}".`
                  : `Tem certeza que deseja deletar o backup "${confirmAction.value.name}"? Esta ação não pode ser desfeita.`}
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="ghost"
                  style={{ padding: '6px 16px', borderRadius: 8, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, cursor: 'pointer' }}
                  onClick={() => { confirmAction.value = null; }}
                  disabled={!!actionLoading.value}
                >
                  Cancelar
                </Button>
                <Button variant="ghost"
                  style={{
                    padding: '6px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
                    background: confirmAction.value.type === 'restore' ? colors.green : colors.red,
                    color: T.text,
                  }}
                  disabled={!!actionLoading.value}
                  onClick={() => {
                    if (confirmAction.value!.type === 'restore') handleRestore(workspaceId, confirmAction.value!.id);
                    else handleDelete(workspaceId, confirmAction.value!.id);
                  }}
                >
                  {actionLoading.value ? 'Aguarde...' : 'Confirmar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
